// ===========================================
// Overview Tab Component - Compact Layout with Inline Editing
// ===========================================
// Full-width stacked layout: Stats Bar → README → Tech Stack
// All project metadata fields are now inline editable

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { 
  FileText, 
  DollarSign,
  Code2,
  Check,
  CreditCard,
  Plus,
  X,
  Loader,
  Sparkles,
  ChevronDown,
  // Icons for project info section
  Globe,
  Eye,
  Tag,
  ExternalLink,
  Github,
  TrendingUp,
  Cpu,
  ChevronRight,
  // Icons for inline editing
  Pencil,
  // Icons for tech stack categories
  Server,
  Database,
  Cloud,
  Bot,
  TestTube
} from 'lucide-react';
import ReadmeUpload from '../ReadmeUpload';
import { projectAPI } from '../../services/api';
import { useProjects } from '../../context/ProjectContext';

// ===========================================
// Category Options
// ===========================================
const CATEGORIES = [
  { value: 'frontend', label: 'Frontend' },
  { value: 'backend', label: 'Backend' },
  { value: 'database', label: 'Database' },
  { value: 'infrastructure', label: 'Infra' },
  { value: 'ai', label: 'AI' },
  { value: 'other', label: 'Other' }
];

// ===========================================
// Category Metadata - Icons and Colors for Visual Grouping
// ===========================================
// Used to display tech stack organized by category with distinct styling
const CATEGORY_META = {
  frontend: { label: 'Frontend', icon: Code2, color: '#3b82f6' },
  backend: { label: 'Backend', icon: Server, color: '#22c55e' },
  database: { label: 'Database', icon: Database, color: '#f59e0b' },
  infrastructure: { label: 'Infra', icon: Cloud, color: '#a855f7' },
  ai: { label: 'AI', icon: Bot, color: '#ec4899' },
  testing: { label: 'Testing', icon: TestTube, color: '#06b6d4' },
  other: { label: 'Other', icon: Code2, color: '#71717a' }
};

// ===========================================
// README Technology Detection - Enhanced
// ===========================================
// Comprehensive pattern matching for 60+ technologies
// Automatically detects tech stack from uploaded README.md content
const detectTechnologiesFromReadme = (readmeContent) => {
  if (!readmeContent) return [];
  
  const content = readmeContent.toLowerCase();
  const detected = [];
  
  // Expanded pattern list for comprehensive tech detection
  const patterns = [
    // ===========================================
    // Frontend Frameworks & Libraries
    // ===========================================
    { name: 'React', pattern: /\breact\b/i, category: 'frontend' },
    { name: 'Next.js', pattern: /\bnext\.?js\b/i, category: 'frontend' },
    { name: 'Vue', pattern: /\bvue\.?js?\b/i, category: 'frontend' },
    { name: 'Nuxt', pattern: /\bnuxt\.?js?\b/i, category: 'frontend' },
    { name: 'Angular', pattern: /\bangular\b/i, category: 'frontend' },
    { name: 'Svelte', pattern: /\bsvelte\b/i, category: 'frontend' },
    { name: 'SvelteKit', pattern: /\bsveltekit\b/i, category: 'frontend' },
    { name: 'Remix', pattern: /\bremix\b/i, category: 'frontend' },
    { name: 'Astro', pattern: /\bastro\b/i, category: 'frontend' },
    { name: 'Solid.js', pattern: /\bsolid\.?js\b/i, category: 'frontend' },
    { name: 'Qwik', pattern: /\bqwik\b/i, category: 'frontend' },
    
    // ===========================================
    // Styling & UI Libraries
    // ===========================================
    { name: 'TypeScript', pattern: /\btypescript\b/i, category: 'frontend' },
    { name: 'Tailwind CSS', pattern: /\btailwind\b/i, category: 'frontend' },
    { name: 'Sass/SCSS', pattern: /\b(sass|scss)\b/i, category: 'frontend' },
    { name: 'Styled Components', pattern: /\bstyled.?components\b/i, category: 'frontend' },
    { name: 'CSS Modules', pattern: /\bcss.?modules\b/i, category: 'frontend' },
    { name: 'shadcn/ui', pattern: /\bshadcn\b/i, category: 'frontend' },
    { name: 'Radix UI', pattern: /\bradix\b/i, category: 'frontend' },
    { name: 'Material UI', pattern: /\bmaterial.?ui\b|\bmui\b/i, category: 'frontend' },
    { name: 'Chakra UI', pattern: /\bchakra\b/i, category: 'frontend' },
    { name: 'Ant Design', pattern: /\bant.?design\b|\bantd\b/i, category: 'frontend' },
    { name: 'Framer Motion', pattern: /\bframer.?motion\b/i, category: 'frontend' },
    
    // ===========================================
    // Backend Frameworks & Languages
    // ===========================================
    { name: 'Node.js', pattern: /\bnode\.?js\b/i, category: 'backend' },
    { name: 'Express', pattern: /\bexpress\.?js?\b/i, category: 'backend' },
    { name: 'Fastify', pattern: /\bfastify\b/i, category: 'backend' },
    { name: 'NestJS', pattern: /\bnest\.?js\b/i, category: 'backend' },
    { name: 'Hono', pattern: /\bhono\b/i, category: 'backend' },
    { name: 'tRPC', pattern: /\btrpc\b/i, category: 'backend' },
    { name: 'Python', pattern: /\bpython\b/i, category: 'backend' },
    { name: 'FastAPI', pattern: /\bfastapi\b/i, category: 'backend' },
    { name: 'Django', pattern: /\bdjango\b/i, category: 'backend' },
    { name: 'Flask', pattern: /\bflask\b/i, category: 'backend' },
    { name: 'Go', pattern: /\b(golang|go\s+lang)\b/i, category: 'backend' },
    { name: 'Rust', pattern: /\b(rust|cargo)\b/i, category: 'backend' },
    { name: 'Ruby on Rails', pattern: /\bruby.?on.?rails\b|\brails\b/i, category: 'backend' },
    { name: 'Bun', pattern: /\bbun\b/i, category: 'backend' },
    { name: 'Deno', pattern: /\bdeno\b/i, category: 'backend' },
    { name: 'GraphQL', pattern: /\bgraphql\b/i, category: 'backend' },
    
    // ===========================================
    // Databases & ORMs
    // ===========================================
    { name: 'MongoDB', pattern: /\bmongodb\b/i, category: 'database' },
    { name: 'PostgreSQL', pattern: /\bpostgres(?:ql)?\b/i, category: 'database' },
    { name: 'MySQL', pattern: /\bmysql\b/i, category: 'database' },
    { name: 'Redis', pattern: /\bredis\b/i, category: 'database' },
    { name: 'Supabase', pattern: /\bsupabase\b/i, category: 'database' },
    { name: 'Firebase', pattern: /\bfirebase\b/i, category: 'database' },
    { name: 'PlanetScale', pattern: /\bplanetscale\b/i, category: 'database' },
    { name: 'Prisma', pattern: /\bprisma\b/i, category: 'database' },
    { name: 'Drizzle', pattern: /\bdrizzle\b/i, category: 'database' },
    { name: 'SQLite', pattern: /\bsqlite\b/i, category: 'database' },
    { name: 'Turso', pattern: /\bturso\b/i, category: 'database' },
    { name: 'Neon', pattern: /\bneon\.?tech\b|\bneon\s+database\b/i, category: 'database' },
    { name: 'Upstash', pattern: /\bupstash\b/i, category: 'database' },
    { name: 'Mongoose', pattern: /\bmongoose\b/i, category: 'database' },
    
    // ===========================================
    // Infrastructure & DevOps
    // ===========================================
    { name: 'Vercel', pattern: /\bvercel\b/i, category: 'infrastructure' },
    { name: 'Netlify', pattern: /\bnetlify\b/i, category: 'infrastructure' },
    { name: 'Railway', pattern: /\brailway\b/i, category: 'infrastructure' },
    { name: 'Render', pattern: /\brender\b/i, category: 'infrastructure' },
    { name: 'Fly.io', pattern: /\bfly\.io\b/i, category: 'infrastructure' },
    { name: 'AWS', pattern: /\baws\b/i, category: 'infrastructure' },
    { name: 'GCP', pattern: /\bgcp\b|\bgoogle.?cloud\b/i, category: 'infrastructure' },
    { name: 'Azure', pattern: /\bazure\b/i, category: 'infrastructure' },
    { name: 'Docker', pattern: /\bdocker\b/i, category: 'infrastructure' },
    { name: 'Kubernetes', pattern: /\bkubernetes\b|\bk8s\b/i, category: 'infrastructure' },
    { name: 'Cloudflare', pattern: /\bcloudflare\b/i, category: 'infrastructure' },
    { name: 'GitHub Actions', pattern: /\bgithub.?actions\b/i, category: 'infrastructure' },
    { name: 'Terraform', pattern: /\bterraform\b/i, category: 'infrastructure' },
    
    // ===========================================
    // AI/ML Services
    // ===========================================
    { name: 'OpenAI', pattern: /\bopenai\b|\bgpt-?\d/i, category: 'ai' },
    { name: 'Claude', pattern: /\bclaude\b|\banthropic\b/i, category: 'ai' },
    { name: 'xAI/Grok', pattern: /\bxai\b|\bgrok\b/i, category: 'ai' },
    { name: 'Vercel AI SDK', pattern: /\bvercel.?ai\b|\bai.?sdk\b/i, category: 'ai' },
    { name: 'LangChain', pattern: /\blangchain\b/i, category: 'ai' },
    { name: 'Pinecone', pattern: /\bpinecone\b/i, category: 'ai' },
    { name: 'Replicate', pattern: /\breplicate\b/i, category: 'ai' },
    { name: 'Hugging Face', pattern: /\bhugging.?face\b/i, category: 'ai' },
    { name: 'Cohere', pattern: /\bcohere\b/i, category: 'ai' },
    { name: 'Ollama', pattern: /\bollama\b/i, category: 'ai' },
    
    // ===========================================
    // Auth & Identity
    // ===========================================
    { name: 'Clerk', pattern: /\bclerk\b/i, category: 'other' },
    { name: 'Auth0', pattern: /\bauth0\b/i, category: 'other' },
    { name: 'NextAuth', pattern: /\bnext.?auth\b/i, category: 'other' },
    { name: 'Supabase Auth', pattern: /\bsupabase.?auth\b/i, category: 'other' },
    { name: 'Firebase Auth', pattern: /\bfirebase.?auth\b/i, category: 'other' },
    
    // ===========================================
    // Payments & Commerce
    // ===========================================
    { name: 'Stripe', pattern: /\bstripe\b/i, category: 'other' },
    { name: 'Lemon Squeezy', pattern: /\blemon.?squeezy\b/i, category: 'other' },
    { name: 'PayPal', pattern: /\bpaypal\b/i, category: 'other' },
    
    // ===========================================
    // Testing & Quality
    // ===========================================
    { name: 'Jest', pattern: /\bjest\b/i, category: 'other' },
    { name: 'Vitest', pattern: /\bvitest\b/i, category: 'other' },
    { name: 'Playwright', pattern: /\bplaywright\b/i, category: 'other' },
    { name: 'Cypress', pattern: /\bcypress\b/i, category: 'other' },
    { name: 'Testing Library', pattern: /\btesting.?library\b/i, category: 'other' },
    
    // ===========================================
    // Email & Communication
    // ===========================================
    { name: 'Resend', pattern: /\bresend\b/i, category: 'other' },
    { name: 'SendGrid', pattern: /\bsendgrid\b/i, category: 'other' },
    { name: 'Postmark', pattern: /\bpostmark\b/i, category: 'other' },
    
    // ===========================================
    // Misc & Build Tools
    // ===========================================
    { name: 'Webpack', pattern: /\bwebpack\b/i, category: 'other' },
    { name: 'Vite', pattern: /\bvite\b/i, category: 'other' },
    { name: 'Turbopack', pattern: /\bturbopack\b/i, category: 'other' },
    { name: 'esbuild', pattern: /\besbuild\b/i, category: 'other' },
    { name: 'Zod', pattern: /\bzod\b/i, category: 'other' },
    { name: 'React Query', pattern: /\breact.?query\b|\btanstack.?query\b/i, category: 'other' },
    { name: 'SWR', pattern: /\bswr\b/i, category: 'other' },
    { name: 'Zustand', pattern: /\bzustand\b/i, category: 'other' },
    { name: 'Redux', pattern: /\bredux\b/i, category: 'other' },
  ];
  
  // Match against content and collect unique technologies
  patterns.forEach(({ name, pattern, category }) => {
    if (pattern.test(content)) {
      detected.push({ name, category, pricingType: 'free', monthlyCost: 0 });
    }
  });
  
  return detected;
};

// ===========================================
// Main Component
// ===========================================
// ===========================================
// Status and Visibility Options for dropdowns
// ===========================================
const STATUS_OPTIONS = [
  { value: 'idea', label: 'Idea' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' }
];

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private' },
  { value: 'internal', label: 'Internal (Team)' },
  { value: 'public', label: 'Public (Portfolio)' }
];

const ARR_OPTIONS = [
  { value: '$0', label: '$0 (Pre-Revenue)' },
  { value: '$10k', label: '$10k ARR' },
  { value: '$100k', label: '$100k ARR' },
  { value: '$1m', label: '$1M ARR' },
  { value: '$10m', label: '$10M ARR' },
  { value: '$100m', label: '$100M ARR' },
  { value: '$1B+', label: '$1B+ ARR' }
];

// ===========================================
// EditableText Component - Inline text/textarea editor
// ===========================================
// Moved outside OverviewTab to prevent re-mounting on parent re-renders
// which was causing onBlur to fire unexpectedly and losing user input
const EditableText = ({ 
  field, 
  value, 
  type = 'text', 
  placeholder, 
  multiline = false,
  editing,
  editValue,
  setEditValue,
  saveField,
  startEdit,
  setEditing
}) => {
  const inputRef = useRef(null);
  const isEditing = editing === field;

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Handle keyboard events in edit mode
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      // Save on Enter for single-line inputs
      e.preventDefault();
      saveField(field, editValue);
    } else if (e.key === 'Escape') {
      // Cancel on Escape
      setEditing(null);
    }
  };

  if (isEditing) {
    if (multiline) {
      return (
        <textarea
          ref={inputRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={() => saveField(field, editValue)}
          onKeyDown={(e) => e.key === 'Escape' && setEditing(null)}
          className="overview-compact__inline-textarea"
          placeholder={placeholder}
          rows={2}
        />
      );
    }
    return (
      <input
        ref={inputRef}
        type={type}
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={() => saveField(field, editValue)}
        onKeyDown={handleKeyDown}
        className="overview-compact__inline-input"
        placeholder={placeholder}
      />
    );
  }

  // Display mode - clickable to edit
  const displayValue = value || '';
  const isEmpty = !displayValue || (Array.isArray(displayValue) && displayValue.length === 0);

  return (
    <div 
      className={`overview-compact__editable ${isEmpty ? 'overview-compact__editable--empty' : ''}`}
      onClick={() => startEdit(field, value)}
      title="Click to edit"
    >
      <span>{isEmpty ? placeholder : displayValue}</span>
      <Pencil size={12} className="overview-compact__edit-icon" />
    </div>
  );
};

// ===========================================
// EditableSelect Component - Inline dropdown editor
// ===========================================
// Moved outside OverviewTab to prevent re-mounting on parent re-renders
const EditableSelect = ({ 
  field, 
  value, 
  options, 
  displayValue,
  editing,
  editValue,
  setEditValue,
  saveField,
  startEdit,
  setEditing
}) => {
  const selectRef = useRef(null);
  const isEditing = editing === field;

  // Auto-focus when entering edit mode
  useEffect(() => {
    if (isEditing && selectRef.current) {
      selectRef.current.focus();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <select
        ref={selectRef}
        value={editValue}
        onChange={(e) => {
          setEditValue(e.target.value);
          // Auto-save on change for selects
          saveField(field, e.target.value);
        }}
        onBlur={() => setEditing(null)}
        onKeyDown={(e) => e.key === 'Escape' && setEditing(null)}
        className="overview-compact__inline-select"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }

  // Display mode - clickable to edit
  return (
    <div 
      className="overview-compact__editable overview-compact__editable--select"
      onClick={() => startEdit(field, value)}
      title="Click to edit"
    >
      <span>{displayValue || value}</span>
      <ChevronDown size={12} className="overview-compact__edit-icon" />
    </div>
  );
};

const OverviewTab = ({ 
  project, 
  showReadmeUpload, 
  setShowReadmeUpload,
  onReadmeExtracted 
}) => {
  const { setCurrentProject, updateProject } = useProjects();
  const projectId = project._id || project.id;

  // ===========================================
  // Inline Edit State - Track which field is being edited
  // ===========================================
  const [editing, setEditing] = useState(null); // Field name being edited
  const [editValue, setEditValue] = useState(''); // Current edit value
  const [savingField, setSavingField] = useState(false); // Saving indicator for inline edits
  
  // ===========================================
  // Collapsible Info Section State
  // ===========================================
  const [infoExpanded, setInfoExpanded] = useState(false); // Collapsed by default for compact view

  // ===========================================
  // Save field inline - Called on blur or enter
  // ===========================================
  const saveField = async (field, value) => {
    // Skip if value unchanged
    const currentValue = project[field];
    if (value === currentValue || (value === '' && !currentValue)) {
      setEditing(null);
      return;
    }

    setSavingField(true);
    try {
      // Handle special field transformations
      let updateData = { [field]: value };
      
      // Tags need to be parsed from comma-separated string to array
      if (field === 'tags') {
        updateData.tags = value ? value.split(',').map(t => t.trim().toLowerCase()).filter(Boolean) : [];
      }
      // AI spend needs to be parsed as number
      if (field === 'aiDevTokenSpend') {
        updateData.aiDevTokenSpend = parseFloat(value) || 0;
      }

      const result = await updateProject(projectId, updateData);
      if (result.success) {
        setCurrentProject(result.data);
      }
    } catch (err) {
      console.error('Failed to save field:', err);
    } finally {
      setSavingField(false);
      setEditing(null);
    }
  };

  // ===========================================
  // Start editing a field - Set state and focus input
  // ===========================================
  const startEdit = (field, currentValue) => {
    // Convert arrays to comma-separated for tags
    let value = currentValue;
    if (field === 'tags' && Array.isArray(currentValue)) {
      value = currentValue.join(', ');
    }
    setEditing(field);
    setEditValue(value || '');
  };

  // ===========================================
  // Shared props for editable components
  // ===========================================
  // These props are passed to EditableText and EditableSelect components
  // which are defined outside OverviewTab to prevent re-mounting issues
  const editableProps = {
    editing,
    editValue,
    setEditValue,
    saveField,
    startEdit,
    setEditing
  };

  // ===========================================
  // Tech Stack State
  // ===========================================
  const [techStack, setTechStack] = useState(project.techStack || []);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newTech, setNewTech] = useState({
    name: '',
    category: 'frontend',
    pricingType: 'free',
    monthlyCost: 0
  });

  // Detected technologies from README
  const [detectedTech, setDetectedTech] = useState([]);

  // ===========================================
  // Edit Tech State - For inline editing of existing tech items
  // ===========================================
  const [editingTech, setEditingTech] = useState(null); // ID of tech being edited
  const [editTechData, setEditTechData] = useState({
    pricingType: 'free',
    monthlyCost: 0,
    category: 'frontend'
  });

  // Sync techStack when project changes
  useEffect(() => {
    setTechStack(project.techStack || []);
  }, [project.techStack]);

  // Detect technologies from README
  useEffect(() => {
    if (project.readmeContent) {
      const detected = detectTechnologiesFromReadme(project.readmeContent);
      const existingNames = techStack.map(t => t.name.toLowerCase());
      const newDetected = detected.filter(t => !existingNames.includes(t.name.toLowerCase()));
      setDetectedTech(newDetected);
    }
  }, [project.readmeContent, techStack]);

  // ===========================================
  // Auto-categorize tech stack on mount if needed
  // ===========================================
  // Checks if tech items lack categories and triggers categorization API
  useEffect(() => {
    const needsCategorization = techStack.length > 0 && 
      techStack.some(tech => !tech.category || tech.category === 'other');
    
    // Only auto-categorize if most items are uncategorized
    const uncategorizedCount = techStack.filter(t => !t.category || t.category === 'other').length;
    const shouldAutoCategorize = uncategorizedCount > techStack.length * 0.5;
    
    if (shouldAutoCategorize && needsCategorization) {
      // Trigger categorization API in background
      projectAPI.categorizeTechStack(projectId)
        .then(response => {
          if (response.data?.data?.project?.techStack) {
            setTechStack(response.data.data.project.techStack);
            setCurrentProject(response.data.data.project);
          }
        })
        .catch(err => {
          // Silent fail - categorization is a nice-to-have
          console.warn('Auto-categorization failed:', err.message);
        });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  // ===========================================
  // Calculate stats
  // ===========================================
  const stats = useMemo(() => {
    const totalCost = techStack.reduce((sum, tech) => sum + (tech.monthlyCost || 0), 0);
    const freeCount = techStack.filter(t => t.pricingType === 'free' || !t.monthlyCost).length;
    const paidCount = techStack.filter(t => t.pricingType !== 'free' && t.monthlyCost > 0).length;
    return { totalCost, freeCount, paidCount, totalCount: techStack.length };
  }, [techStack]);

  // ===========================================
  // Calculate category breakdown for visualization
  // ===========================================
  // Groups tech by category and calculates counts/percentages for breakdown bar
  const categoryBreakdown = useMemo(() => {
    if (techStack.length === 0) return [];
    
    // Count technologies per category
    const counts = {};
    techStack.forEach(tech => {
      const cat = tech.category || 'other';
      counts[cat] = (counts[cat] || 0) + 1;
    });
    
    // Convert to array with metadata, sorted by count (descending)
    return Object.entries(counts)
      .map(([category, count]) => ({
        category,
        count,
        percentage: Math.round((count / techStack.length) * 100),
        meta: CATEGORY_META[category] || CATEGORY_META.other
      }))
      .sort((a, b) => b.count - a.count);
  }, [techStack]);

  // ===========================================
  // Group tech by category for organized display
  // ===========================================
  const groupedTech = useMemo(() => {
    const groups = {};
    // Initialize all categories with empty arrays
    CATEGORIES.forEach(cat => {
      groups[cat.value] = [];
    });
    // Group technologies by their category
    techStack.forEach(tech => {
      const category = tech.category || 'other';
      if (groups[category]) {
        groups[category].push(tech);
      } else {
        groups['other'].push(tech);
      }
    });
    return groups;
  }, [techStack]);

  // ===========================================
  // Save tech stack
  // ===========================================
  const saveTechStack = async (newStack) => {
    setSaving(true);
    try {
      const response = await projectAPI.update(projectId, { techStack: newStack });
      if (response.data?.data?.project) {
        setCurrentProject(response.data.data.project);
        setTechStack(response.data.data.project.techStack || []);
      }
    } catch (err) {
      console.error('Failed to save tech stack:', err);
    } finally {
      setSaving(false);
    }
  };

  // ===========================================
  // Add technology
  // ===========================================
  const handleAddTech = async () => {
    if (!newTech.name.trim()) return;
    
    const techToAdd = {
      ...newTech,
      name: newTech.name.trim(),
      monthlyCost: newTech.pricingType === 'free' ? 0 : parseFloat(newTech.monthlyCost) || 0,
      billingCycle: 'monthly'
    };
    
    const newStack = [...techStack, techToAdd];
    await saveTechStack(newStack);
    
    setNewTech({ name: '', category: 'frontend', pricingType: 'free', monthlyCost: 0 });
    setShowAddForm(false);
  };

  // ===========================================
  // Add detected technology
  // ===========================================
  const handleAddDetected = async (tech) => {
    const newStack = [...techStack, tech];
    setDetectedTech(prev => prev.filter(t => t.name !== tech.name));
    await saveTechStack(newStack);
  };

  // ===========================================
  // Remove technology
  // ===========================================
  const handleRemoveTech = async (techId) => {
    const newStack = techStack.filter(t => (t._id || t.id || t.name) !== techId);
    await saveTechStack(newStack);
  };

  // ===========================================
  // Start editing a tech item - Opens inline edit form
  // ===========================================
  const handleStartEditTech = (tech) => {
    const techId = tech._id || tech.id || tech.name;
    setEditingTech(techId);
    setEditTechData({
      pricingType: tech.pricingType || 'free',
      monthlyCost: tech.monthlyCost || 0,
      category: tech.category || 'frontend'
    });
  };

  // ===========================================
  // Save edited tech item - Updates pricing/cost info
  // ===========================================
  const handleSaveEditTech = async () => {
    if (!editingTech) return;
    
    // Find and update the tech item in the stack
    const newStack = techStack.map(tech => {
      const techId = tech._id || tech.id || tech.name;
      if (techId === editingTech) {
        return {
          ...tech,
          pricingType: editTechData.pricingType,
          monthlyCost: editTechData.pricingType === 'free' ? 0 : parseFloat(editTechData.monthlyCost) || 0,
          category: editTechData.category
        };
      }
      return tech;
    });
    
    await saveTechStack(newStack);
    setEditingTech(null);
  };

  // ===========================================
  // Cancel editing tech item
  // ===========================================
  const handleCancelEditTech = () => {
    setEditingTech(null);
    setEditTechData({ pricingType: 'free', monthlyCost: 0, category: 'frontend' });
  };

  // ===========================================
  // Get pricing display
  // ===========================================
  const getPricingDisplay = (tech) => {
    if (tech.pricingType === 'free' || !tech.monthlyCost) {
      return { label: 'free', className: 'free' };
    }
    if (tech.pricingType === 'consumption') {
      return { label: `~$${tech.monthlyCost}`, className: 'consumption' };
    }
    return { label: `$${tech.monthlyCost}`, className: 'subscription' };
  };

  // ===========================================
  // Truncate description for preview
  // ===========================================
  const truncateText = (text, maxLength = 80) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <div className="overview-compact">
      {/* ===========================================
          Project Info Section - Collapsible with Preview
          =========================================== */}
      <div className={`overview-compact__info-section ${infoExpanded ? 'overview-compact__info-section--expanded' : ''}`}>
        {/* Saving indicator */}
        {savingField && (
          <div className="overview-compact__saving">
            <Loader size={12} className="overview-compact__spinner" />
            <span>Saving...</span>
          </div>
        )}

        {/* Collapsed Header - Always visible, click to expand */}
        <div 
          className="overview-compact__info-header"
          onClick={() => setInfoExpanded(!infoExpanded)}
        >
          <div className="overview-compact__info-preview">
            {/* Status badge */}
            <span className={`overview-compact__status overview-compact__status--${project.status}`}>
              {project.status || 'idea'}
            </span>
            
            {/* Description preview */}
            <span className="overview-compact__desc-preview">
              {truncateText(project.description) || 'No description'}
            </span>

            {/* Quick stats preview */}
            <div className="overview-compact__quick-stats">
              {project.aiDevTokenSpend > 0 && (
                <span className="overview-compact__quick-stat">
                  <Cpu size={10} /> ~${project.aiDevTokenSpend}
                </span>
              )}
              {project.arrLevel && project.arrLevel !== '$0' && (
                <span className="overview-compact__quick-stat overview-compact__quick-stat--arr">
                  <TrendingUp size={10} /> {project.arrLevel}
                </span>
              )}
            </div>
          </div>

          <div className="overview-compact__info-actions">
            {/* Quick link buttons */}
            {project.repoUrl && (
              <a 
                href={project.repoUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="overview-compact__quick-link"
                onClick={(e) => e.stopPropagation()}
              >
                <Github size={14} />
                <span>Repo</span>
              </a>
            )}
            {project.liveUrl && (
              <a 
                href={project.liveUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="overview-compact__quick-link overview-compact__quick-link--live"
                onClick={(e) => e.stopPropagation()}
              >
                <Globe size={14} />
                <span>Live</span>
              </a>
            )}
            
            {/* Expand/collapse toggle */}
            <button className="overview-compact__expand-btn">
              <ChevronRight 
                size={16} 
                style={{ transform: infoExpanded ? 'rotate(90deg)' : 'none' }} 
              />
            </button>
          </div>
        </div>

        {/* Expanded Content - All editable fields */}
        {infoExpanded && (
          <div className="overview-compact__info-grid">
            {/* Description - editable textarea */}
            <div className="overview-compact__info-full">
              <span className="overview-compact__info-label">Description</span>
              <EditableText 
                field="description" 
                value={project.description} 
                multiline={true}
                placeholder="Add a project description..."
                {...editableProps}
              />
            </div>
            
            {/* Two-column row: Status & Visibility */}
            <div className="overview-compact__info-row">
              <div className="overview-compact__info-item overview-compact__info-item--editable">
                <span className="overview-compact__info-label">Status</span>
                <EditableSelect 
                  field="status" 
                  value={project.status || 'idea'} 
                  options={STATUS_OPTIONS}
                  displayValue={
                    <span className={`overview-compact__status overview-compact__status--${project.status}`}>
                      {project.status || 'idea'}
                    </span>
                  }
                  {...editableProps}
                />
              </div>
              <div className="overview-compact__info-item overview-compact__info-item--editable">
                <Eye size={12} />
                <span className="overview-compact__info-label">Visibility</span>
                <EditableSelect 
                  field="visibility" 
                  value={project.visibility || 'private'} 
                  options={VISIBILITY_OPTIONS}
                  displayValue={project.visibility || 'private'}
                  {...editableProps}
                />
              </div>
            </div>

            {/* Tags */}
            <div className="overview-compact__info-full">
              <span className="overview-compact__info-label">
                <Tag size={12} /> Tags
              </span>
              {editing === 'tags' ? (
                <EditableText 
                  field="tags" 
                  value={project.tags} 
                  placeholder="saas, fintech, ai (comma-separated)"
                  {...editableProps}
                />
              ) : (
                <div 
                  className={`overview-compact__tags overview-compact__editable ${!project.tags?.length ? 'overview-compact__editable--empty' : ''}`}
                  onClick={() => startEdit('tags', project.tags)}
                  title="Click to edit tags"
                >
                  {project.tags?.length > 0 ? (
                    project.tags.map((tag, idx) => (
                      <span key={idx} className="overview-compact__tag">{tag}</span>
                    ))
                  ) : (
                    <span className="overview-compact__placeholder">Add tags (comma-separated)...</span>
                  )}
                  <Pencil size={12} className="overview-compact__edit-icon" />
                </div>
              )}
            </div>

            {/* Investment Row: AI Spend & ARR */}
            <div className="overview-compact__info-row">
              <div className="overview-compact__info-item overview-compact__info-item--editable">
                <Cpu size={12} />
                <span className="overview-compact__info-label">Est. AI Cost</span>
                <div className="overview-compact__info-value--editable">
                  <span className="overview-compact__currency">~$</span>
                  <EditableText 
                    field="aiDevTokenSpend" 
                    value={project.aiDevTokenSpend || ''} 
                    type="number"
                    placeholder="0"
                    {...editableProps}
                  />
                </div>
              </div>
              <div className="overview-compact__info-item overview-compact__info-item--editable">
                <TrendingUp size={12} />
                <span className="overview-compact__info-label">Current ARR</span>
                <EditableSelect 
                  field="arrLevel" 
                  value={project.arrLevel || '$0'} 
                  options={ARR_OPTIONS}
                  displayValue={
                    <span className="overview-compact__info-value overview-compact__info-value--arr">
                      {project.arrLevel || '$0'}
                    </span>
                  }
                  {...editableProps}
                />
              </div>
            </div>

            {/* URLs Row */}
            <div className="overview-compact__info-urls">
              <div className="overview-compact__url-item">
                <Github size={14} />
                <span className="overview-compact__url-label">Repo</span>
                <EditableText 
                  field="repoUrl" 
                  value={project.repoUrl} 
                  type="url"
                  placeholder="https://github.com/..."
                  {...editableProps}
                />
                {project.repoUrl && (
                  <a href={project.repoUrl} target="_blank" rel="noopener noreferrer" className="overview-compact__url-open">
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
              <div className="overview-compact__url-item">
                <Globe size={14} />
                <span className="overview-compact__url-label">Live</span>
                <EditableText 
                  field="liveUrl" 
                  value={project.liveUrl} 
                  type="url"
                  placeholder="https://..."
                  {...editableProps}
                />
                {project.liveUrl && (
                  <a href={project.liveUrl} target="_blank" rel="noopener noreferrer" className="overview-compact__url-open">
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stats Bar - Horizontal quick glance */}
      <div className="overview-compact__stats-bar">
        <div className="overview-compact__stat">
          <DollarSign size={14} />
          <span className="overview-compact__stat-value">${stats.totalCost.toFixed(2)}</span>
          <span className="overview-compact__stat-label">/mo</span>
        </div>
        <div className="overview-compact__stat-divider" />
        <div className="overview-compact__stat">
          <Code2 size={14} />
          <span className="overview-compact__stat-value">{stats.totalCount}</span>
          <span className="overview-compact__stat-label">tech</span>
        </div>
        <div className="overview-compact__stat-divider" />
        <div className="overview-compact__stat overview-compact__stat--success">
          <Check size={14} />
          <span className="overview-compact__stat-value">{stats.freeCount}</span>
          <span className="overview-compact__stat-label">free</span>
        </div>
        <div className="overview-compact__stat-divider" />
        <div className="overview-compact__stat overview-compact__stat--purple">
          <CreditCard size={14} />
          <span className="overview-compact__stat-value">{stats.paidCount}</span>
          <span className="overview-compact__stat-label">paid</span>
        </div>
      </div>

      {/* ===========================================
          Summarized README.MD Section - AI-Generated Summary
          Displays above README & Tech Stack when available
          =========================================== */}
      {project.readmeSummary && (
        <div className="overview-compact__summary-section">
          <div className="overview-compact__section-header">
            <div className="overview-compact__section-title">
              <Sparkles size={14} />
              <span>Summarized README.MD</span>
            </div>
          </div>
          <div className="overview-compact__summary-content">
            {/* Parse and render the summary with proper formatting */}
            {project.readmeSummary.split('\n').map((line, idx) => {
              // Render bold headers (lines starting with **)
              if (line.startsWith('**') && line.includes(':**')) {
                const headerText = line.replace(/\*\*/g, '').replace(':', '');
                return (
                  <h4 key={idx} className="overview-compact__summary-header">
                    {headerText}
                  </h4>
                );
              }
              // Render bullet points
              if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
                return (
                  <li key={idx} className="overview-compact__summary-bullet">
                    {line.replace(/^[•\-*]\s*/, '')}
                  </li>
                );
              }
              // Render regular text
              if (line.trim()) {
                return <p key={idx} className="overview-compact__summary-text">{line}</p>;
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Two-column layout: README and Tech Stack side by side */}
      <div className="overview-compact__two-column">
        {/* README Section */}
        <div className="overview-compact__section">
          <div className="overview-compact__section-header">
            <div className="overview-compact__section-title">
              <FileText size={14} />
              <span>README.md</span>
            </div>
            {project.readmeContent && (
              <button 
                className="overview-compact__section-action"
                onClick={() => setShowReadmeUpload(!showReadmeUpload)}
              >
                {showReadmeUpload ? 'Close' : 'Re-import'}
                <ChevronDown size={12} style={{ transform: showReadmeUpload ? 'rotate(180deg)' : 'none' }} />
              </button>
            )}
          </div>
          
          <div className="overview-compact__readme">
            {project.readmeContent && !showReadmeUpload ? (
              <div className="overview-compact__readme-content">
                <pre>{project.readmeContent}</pre>
              </div>
            ) : (
              <ReadmeUpload 
                projectId={projectId}
                onDataExtracted={onReadmeExtracted}
                onClose={() => setShowReadmeUpload(false)}
              />
            )}
          </div>
        </div>

        {/* Tech Stack Section */}
        <div className="overview-compact__section">
        <div className="overview-compact__section-header">
          <div className="overview-compact__section-title">
            <Code2 size={14} />
            <span>Tech Stack</span>
            {saving && <Loader size={12} className="overview-compact__spinner" />}
          </div>
          <div className="overview-compact__section-meta">
            <span className="overview-compact__cost">${stats.totalCost.toFixed(2)}/mo</span>
            <button 
              className="btn-primary btn-xs"
              onClick={() => setShowAddForm(!showAddForm)}
            >
              <Plus size={12} />
              Add
            </button>
          </div>
        </div>

        {/* Stack Breakdown Bar - Visual category distribution */}
        {categoryBreakdown.length > 0 && (
          <div className="overview-compact__stack-breakdown">
            {/* Horizontal bar showing category proportions */}
            <div className="overview-compact__breakdown-bar">
              {categoryBreakdown.map(({ category, percentage, meta }) => (
                <div 
                  key={category}
                  className="overview-compact__breakdown-segment"
                  style={{ 
                    width: `${Math.max(percentage, 8)}%`, // Min 8% for visibility
                    backgroundColor: meta.color 
                  }}
                  title={`${meta.label}: ${percentage}%`}
                />
              ))}
            </div>
            {/* Category labels with counts */}
            <div className="overview-compact__breakdown-labels">
              {categoryBreakdown.map(({ category, count, meta }) => {
                const CategoryIcon = meta.icon;
                return (
                  <div 
                    key={category} 
                    className="overview-compact__breakdown-label"
                    style={{ color: meta.color }}
                  >
                    <CategoryIcon size={10} />
                    <span>{meta.label}</span>
                    <span className="overview-compact__breakdown-count">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Detected from README */}
        {detectedTech.length > 0 && (
          <div className="overview-compact__detected">
            <Sparkles size={12} />
            <span>Detected:</span>
            {detectedTech.slice(0, 6).map((tech, idx) => (
              <button
                key={idx}
                className="overview-compact__detected-btn"
                onClick={() => handleAddDetected(tech)}
              >
                + {tech.name}
              </button>
            ))}
          </div>
        )}

        {/* Add Form */}
        {showAddForm && (
          <div className="overview-compact__add-form">
            <input
              type="text"
              placeholder="Technology name..."
              value={newTech.name}
              onChange={(e) => setNewTech({ ...newTech, name: e.target.value })}
              className="overview-compact__input"
              autoFocus
            />
            <select
              value={newTech.category}
              onChange={(e) => setNewTech({ ...newTech, category: e.target.value })}
              className="overview-compact__select"
            >
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
            <div className="overview-compact__pricing-btns">
              {['free', 'subscription', 'consumption'].map(type => (
                <button
                  key={type}
                  className={`overview-compact__pricing-btn ${newTech.pricingType === type ? 'active' : ''}`}
                  onClick={() => setNewTech({ ...newTech, pricingType: type, monthlyCost: type === 'free' ? 0 : newTech.monthlyCost })}
                >
                  {type === 'free' ? 'Free' : type === 'subscription' ? 'Sub' : 'Use'}
                </button>
              ))}
            </div>
            {newTech.pricingType !== 'free' && (
              <div className="overview-compact__cost-input">
                <span>$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={newTech.monthlyCost || ''}
                  onChange={(e) => setNewTech({ ...newTech, monthlyCost: parseFloat(e.target.value) || 0 })}
                />
                <span>/mo</span>
              </div>
            )}
            <div className="overview-compact__form-actions">
              <button className="btn-secondary btn-xs" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button 
                className="btn-primary btn-xs" 
                onClick={handleAddTech}
                disabled={!newTech.name.trim()}
              >
                Add
              </button>
            </div>
          </div>
        )}

        {/* Tech Stack - Grouped by Category */}
        <div className="overview-compact__tech-groups">
          {techStack.length > 0 ? (
            // Render each category that has technologies
            CATEGORIES.map(cat => {
              const techs = groupedTech[cat.value];
              // Skip empty categories
              if (!techs || techs.length === 0) return null;
              
              // Get category metadata for icon and color
              const meta = CATEGORY_META[cat.value] || CATEGORY_META.other;
              const CategoryIcon = meta.icon;
              
              return (
                <div key={cat.value} className="overview-compact__tech-group">
                  {/* Category label with icon */}
                  <div 
                    className="overview-compact__tech-group-label" 
                    style={{ color: meta.color }}
                  >
                    <CategoryIcon size={12} />
                    <span>{meta.label}</span>
                  </div>
                  {/* Tech pills for this category */}
                  <div className="overview-compact__tech-group-pills">
                    {techs.map(tech => {
                      const techId = tech._id || tech.id || tech.name;
                      const pricing = getPricingDisplay(tech);
                      const isEditing = editingTech === techId;
                      
                      // Render inline edit form when this tech is being edited
                      if (isEditing) {
                        return (
                          <div key={techId} className="tech-pill tech-pill--editing">
                            <span className="tech-pill__name">{tech.name}</span>
                            {/* Category selector */}
                            <select
                              className="tech-pill__edit-select"
                              value={editTechData.category}
                              onChange={(e) => setEditTechData({ ...editTechData, category: e.target.value })}
                            >
                              {CATEGORIES.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                              ))}
                            </select>
                            {/* Pricing type buttons */}
                            <div className="tech-pill__edit-pricing">
                              {['free', 'subscription', 'consumption'].map(type => (
                                <button
                                  key={type}
                                  className={`tech-pill__pricing-btn ${editTechData.pricingType === type ? 'active' : ''}`}
                                  onClick={() => setEditTechData({ 
                                    ...editTechData, 
                                    pricingType: type, 
                                    monthlyCost: type === 'free' ? 0 : editTechData.monthlyCost 
                                  })}
                                >
                                  {type === 'free' ? 'Free' : type === 'subscription' ? '$' : '~$'}
                                </button>
                              ))}
                            </div>
                            {/* Cost input - only show if not free */}
                            {editTechData.pricingType !== 'free' && (
                              <div className="tech-pill__edit-cost">
                                <span>$</span>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editTechData.monthlyCost || ''}
                                  onChange={(e) => setEditTechData({ 
                                    ...editTechData, 
                                    monthlyCost: parseFloat(e.target.value) || 0 
                                  })}
                                  placeholder="0"
                                  autoFocus
                                />
                                <span>/mo</span>
                              </div>
                            )}
                            {/* Save/Cancel buttons */}
                            <button 
                              className="tech-pill__edit-save"
                              onClick={handleSaveEditTech}
                              disabled={saving}
                            >
                              <Check size={12} />
                            </button>
                            <button 
                              className="tech-pill__edit-cancel"
                              onClick={handleCancelEditTech}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        );
                      }
                      
                      // Regular display mode - clickable to edit
                      return (
                        <div 
                          key={techId} 
                          className="tech-pill tech-pill--compact tech-pill--clickable"
                          onClick={() => handleStartEditTech(tech)}
                          title="Click to edit pricing"
                        >
                          <span className="tech-pill__name">{tech.name}</span>
                          <span className={`tech-pill__price tech-pill__price--${pricing.className}`}>
                            {pricing.label}
                          </span>
                          <button 
                            className="tech-pill__remove"
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent edit mode from triggering
                              handleRemoveTech(techId);
                            }}
                          >
                            <X size={10} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="overview-compact__empty">
              No technologies added yet
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
